import * as React from "react";
import * as S from "@styled";
import * as _ from "lodash";
import CustomColorSwitcher, { ICustomColorSwitch, ICustomColorConfiguration } from "shared/components/CustomColorSwitcher";
import useUserTheme from "shared/styles/hooks/useUserTheme";
import { HashMap } from "shared/model/HashMap";
import { ThemeType } from "shared/styles/theme";
import colors from "shared/styles/colors";
import { Utilities } from "shared/utilities";
import { IAppReducerState } from "environments/shared/model";
import { AppContext } from "environments/App";
import { useParams } from "react-router";
import { Permissions } from "shared/model/AllPermissions";
import { SaveBar } from "./detailLevels/Shared";

const EnvironmentColorSelector: React.FC<IEnvironmentColorSelectorProps> = (props) => {
    const { environment } = useParams<{ environment: string }>();
    const { state } = React.useContext<IAppReducerState>(AppContext);
    const environmentData = state?.environments.find((env) => env.name === environment);

    const [theme] = useUserTheme();

    const existingOrDefaultColor = environmentData?.color || "default";

    const [color, setColor] = React.useState<EnvironmentColor | undefined>(existingOrDefaultColor);

    const updateColor = async () => {
        try {
            await Utilities.patchJsonAndParseResponse<string>(`api/environment/${props.environmenName}/changeColor`, { color });
            location.reload();
        }
        catch (error) {
            console.error(error);
            Utilities.toastError(error);
        }
    }

    const colors: EnvironmentColor[] = ['purple', 'orange', 'pink', 'blue', 'green', "default"];
    const colorPairs: ICustomColorSwitch[] = colors.map((colorPair) => {
        const switchColor = getSwitchColorByTheme(theme, colorPair);
        return {
            backgroundColor: switchColor.backgroundColor,
            borderColor: switchColor.borderColor,
            color: switchColor.color,
            onClick: () => setColor(colorPair),
            selected: _.isEqual(color, colorPair),
            asDisabled: switchColor.asDisabled
        }
    });

    const isDirty = color !== existingOrDefaultColor;

    const isEnvironmentAdmin = Utilities.getUserPermission(Permissions.EnvironmentAdmin);
    if (!isEnvironmentAdmin) {
        return null;
    }

    return (
        <>
            <S.FormGroup style={{ marginTop: 24 }}>
                <S.Label>Pick a color for the environment</S.Label>
                <CustomColorSwitcher
                    colors={colorPairs}
                />
            </S.FormGroup>
            <SaveBar
                isShown={isDirty && !state.isLoading}
                handleSave={() => updateColor()}
            />
        </>
    )
}

export const environmentColorMap: HashMap<IEnvironmentColorMap> = {
    [ThemeType.light]: {
        "purple": {
            backgroundColor: colors.purpleBackground,
            borderColor: colors.purplePrimary,
            color: colors.purplePrimary
        },
        "orange": {
            backgroundColor: colors.orangeBackground,
            borderColor: colors.orangePrimary,
            color: colors.orangeDark
        },
        "pink": {
            backgroundColor: colors.pinkBackground,
            borderColor: colors.pinkPrimary,
            color: colors.pinkDark
        },
        "blue": {
            backgroundColor: colors.blueBackground,
            borderColor: colors.bluePrimary,
            color: colors.blueDark
        },
        "green": {
            backgroundColor: colors.greenBackground,
            borderColor: colors.greenPrimary,
            color: colors.greenDark
        },
        "default": {
            backgroundColor: colors.grey_4,
            borderColor: colors.grey_2,
            color: colors.white,
            asDisabled: true
        }
    },
    [ThemeType.dark]: {
        "purple": {
            backgroundColor: colors.darkOrangeBackground,
            borderColor: colors.darkOrangePrimary,
            color: colors.darkOrangePrimary
        },
        "orange": {
            backgroundColor: colors.darkGreenBackground,
            borderColor: colors.darkGreenPrimary,
            color: colors.darkGreenBackground
        },
        "pink": {
            backgroundColor: colors.darkBlueBackground,
            borderColor: colors.darkBluePrimary,
            color: colors.darkBlueBackground
        },
        "blue": {
            backgroundColor: colors.darkPurpleTagBackground,
            borderColor: colors.darkPurpleTagBorder,
            color: colors.darkPurpleTagBackground
        },
        "green": {
            backgroundColor: colors.darkWarningBackground,
            borderColor: colors.darkWarningPrimary,
            color: colors.darkWarningBackground
        },
        "default": {
            backgroundColor: colors.grey_1,
            borderColor: colors.grey_2,
            color: colors.dark3,
            asDisabled: true
        },
    }
};

export type IEnvironmentColorMap = {
    [color in EnvironmentColor]: ICustomColorConfiguration;
}

export type EnvironmentColor = "purple" | "orange" | "pink" | "blue" | "green" | "default";

export const getSwitchColorByTheme = (theme: ThemeType, color: EnvironmentColor): ICustomColorConfiguration => {
    return environmentColorMap[theme][color];
}

interface IEnvironmentColorSelectorProps {
    environmenName?: string;
}

export default EnvironmentColorSelector;